/* Contractor Portal — account details + password change. */
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Building2, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContractorAuth } from "@/context/ContractorAuthContext";

const inputCls = "w-full bg-transparent border border-[#2C313A] text-white mono px-3 py-2 text-sm focus:outline-none focus:border-[#38BDF8]";

/* PAIRED TRUCK — a pump crew works with one truck day in, day out. Pair it here and
   you get that truck's permanent tracker link, plus your crew's details land on the
   truck so the supplier's dispatcher and driver know who they're pouring with. */
function PairedTruck() {
  const [opts, setOpts] = useState([]);
  const [cur, setCur] = useState(null);
  const [f, setF] = useState({ truck_id: "", crew_name: "", crew_phone: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([api.get("/partner/trucks"), api.get("/partner/paired-truck")]);
      setOpts(o.data.trucks || []);
      setCur(c.data);
      setF({ truck_id: c.data.truck_id || "", crew_name: c.data.crew_name || "", crew_phone: c.data.crew_phone || "" });
    } catch { /* not connected to anyone yet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setCur((await api.put("/partner/paired-truck", f)).data); toast.success("Truck paired"); }
    catch (ex) { toast.error(ex.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#0B0D10] border border-[#2C313A] focus:border-[#38BDF8] outline-none text-white px-3 py-2.5 text-sm";
  return (
    <form onSubmit={save} className="bg-[#12151A] border border-[#22262E] p-5 space-y-3" data-testid="ctr-paired-truck">
      <div>
        <div className="text-[10px] tracking-widest uppercase text-[#8B909A]">Our truck</div>
        <div className="text-white font-bold">Pair your crew with a truck</div>
        <p className="text-[12px] text-[#A1A6B0] mt-1">Pick the truck you normally pour with. You'll get its permanent tracker link, and your crew's name and number go onto that truck for the plant's dispatcher and driver.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="sm:col-span-1">
          <span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Truck</span>
          <select data-testid="ctr-paired-truck-select" value={f.truck_id} onChange={(e) => setF({ ...f, truck_id: e.target.value })} className={inputCls}>
            <option value="">— none —</option>
            {opts.map((t) => <option key={t.id} value={t.id}>{t.number}{t.rego ? ` · ${t.rego}` : ""} · {t.supplier}</option>)}
          </select>
        </label>
        <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Crew / operator</span>
          <input data-testid="ctr-crew-name" value={f.crew_name} onChange={(e) => setF({ ...f, crew_name: e.target.value })} className={inputCls} /></label>
        <label><span className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Mobile</span>
          <input data-testid="ctr-crew-phone" value={f.crew_phone} onChange={(e) => setF({ ...f, crew_phone: e.target.value })} className={inputCls} /></label>
      </div>
      {opts.length === 0 && <div className="text-[12px] text-amber-300" data-testid="ctr-paired-no-trucks">No trucks yet — connect to a concrete supplier first and their trucks will show here.</div>}
      {cur?.url && (
        <div className="border border-[#2C313A] p-3 space-y-2" data-testid="ctr-paired-truck-link">
          <div className="text-[10px] tracking-widest uppercase text-[#8B909A]">{cur.number} · permanent tracker</div>
          <div className="mono text-[11px] break-all text-[#38BDF8]">{cur.url}</div>
          <div className="flex gap-2">
            <button type="button" data-testid="ctr-paired-copy" onClick={() => { navigator.clipboard?.writeText(cur.url); toast.success("Copied"); }}
              className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white">Copy link</button>
            <a href={cur.url} target="_blank" rel="noreferrer" data-testid="ctr-paired-open"
              className="mono text-[10px] uppercase tracking-widest border border-[#2C313A] px-3 py-1.5 text-[#A1A6B0] hover:text-white">Open</a>
          </div>
        </div>
      )}
      <button type="submit" disabled={busy} data-testid="ctr-paired-save" className="bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-xs px-4 py-2.5 disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
    </form>
  );
}

export default function ContractorAccount() {
  const { user, refresh } = useContractorAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const change = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/contractor/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated"); setCur(""); setPw(""); setPw2(""); refresh();
    } catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-3xl" data-testid="contractor-account-page">
      <div>
        <div className="text-[10px] tracking-[0.25em] text-[#8B909A]">PROFILE</div>
        <h1 className="heading text-2xl sm:text-3xl font-black uppercase text-white">My <span className="text-[#38BDF8]">Account</span></h1>
      </div>

      {user?.must_change_password && (
        <div className="border border-amber-700 bg-amber-950/30 text-amber-200 text-sm p-3" data-testid="ctr-must-change-banner">You're signed in with a temporary password — please set your own below.</div>
      )}

      <div className="bg-[#12151A] border border-[#22262E] p-5 grid sm:grid-cols-2 gap-4" data-testid="ctr-account-details">
        <div className="sm:col-span-2 flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#8B909A]"><Building2 className="w-3 h-3 text-[#38BDF8]" /> Contractor</div>
        <div><div className="text-[9px] tracking-widest uppercase text-[#8B909A]">Company</div><div className="text-white font-bold" data-testid="ctr-account-company">{user?.contractor_name}</div></div>
        <div><div className="text-[9px] tracking-widest uppercase text-[#8B909A]">ABN</div><div className="mono text-[#E5E7EB]">{user?.abn || "—"}</div></div>
        <div><div className="text-[9px] tracking-widest uppercase text-[#8B909A]">Contact</div><div className="text-[#E5E7EB]">{user?.name}</div></div>
        <div><div className="text-[9px] tracking-widest uppercase text-[#8B909A]">Login email</div><div className="mono text-[#E5E7EB]" data-testid="ctr-account-email">{user?.email}</div></div>
        <div className="sm:col-span-2 flex items-center gap-2 text-xs text-emerald-400"><ShieldCheck className="w-4 h-4" /> Approved by {user?.approved_org_count} plant{user?.approved_org_count === 1 ? "" : "s"} · you only ever see your own linked trucks.</div>
      </div>

      <PairedTruck />

      <form onSubmit={change} className="bg-[#12151A] border border-[#22262E] p-5 space-y-3" data-testid="ctr-change-password-form">
        <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#8B909A]"><KeyRound className="w-3 h-3 text-[#38BDF8]" /> Security · change password</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Current</label><input data-testid="ctr-pw-current" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} autoComplete="current-password" /></div>
          <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">New (8+ chars)</label><input data-testid="ctr-pw-new" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} autoComplete="new-password" /></div>
          <div><label className="block text-[10px] tracking-widest uppercase text-[#8B909A] mb-1">Confirm</label><input data-testid="ctr-pw-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputCls} autoComplete="new-password" /></div>
        </div>
        <button data-testid="ctr-pw-submit" type="submit" disabled={busy} className="bg-[#38BDF8] text-[#0B0D10] hover:bg-[#7DD3FC] font-black uppercase tracking-widest text-xs px-4 py-2.5 disabled:opacity-50">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
}
